import React, { useEffect, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { ReceiptModal } from "./ReceiptModal";
import { Eye, Check, X, CornerUpLeft, FileDown } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const mondayOf = (d) => { const dt = new Date(d); const day = (dt.getDay() + 6) % 7; dt.setDate(dt.getDate() - day); return dt.toISOString().slice(0, 10); };
const STATUS_COLORS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700", returned: "bg-stone-200 text-stone-700" };

export default function ExpenseApproval() {
  const [week, setWeek] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState([]);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/expenses?week_start=${week}&backlog=1`);
    setRows(data); setSel([]);
  };

  const downloadPdf = async () => {
    const token = getToken();
    const res = await fetch(`${API}/expenses/export/weekly-pdf?week_start=${week}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `expenses_week_${week}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  useEffect(() => { load(); }, [week]);

  const act = async (id, action, needComment) => {
    let comments = null;
    if (needComment) { comments = window.prompt(`Comments (required to ${action}):`); if (!comments) return; }
    await api.post(`/expenses/${id}/${action}`, { comments });
    await load();
    setViewing(null);
  };
  const bulkApprove = async () => { if (!sel.length) return; await api.post("/expenses/bulk-approve", { ids: sel }); await load(); };
  const setReimb = async (id, status) => { await api.put(`/expenses/${id}/reimbursement`, { reimbursement_status: status }); await load(); const e = rows.find((r) => r.id === id); if (e) setViewing({ ...e, reimbursement_status: status }); };

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const pendingIds = rows.filter((r) => r.status === "pending").map((r) => r.id);

  return (
    <div className="space-y-4" data-testid="expense-approval">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-stone-500 block mb-1">Payroll Week (Mon)</label>
          <input data-testid="approval-week" type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))} className="h-12 px-3 border border-stone-200" />
        </div>
        <button data-testid="bulk-approve-btn" onClick={bulkApprove} disabled={!sel.length} className="h-12 px-5 bg-[#1F3D34] text-white font-bold uppercase text-sm disabled:opacity-40">Approve Selected ({sel.length})</button>
        <button data-testid="week-pdf-btn" onClick={downloadPdf} className="h-12 px-5 bg-white border border-stone-300 font-bold uppercase text-sm flex items-center gap-2 hover:border-[#1F3D34]"><FileDown size={16} /> Week PDF + Receipts</button>
      </div>
      <p className="text-xs text-stone-500 -mt-2">Unapproved expenses from previous weeks always appear here until approved. The PDF includes every approved expense for this week plus prior-week expenses approved this week, each with its receipt photo.</p>

      <div className="bg-white border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b-2 border-stone-300">
            <tr className="text-left text-xs tracking-wide">
              <th className="p-3"><input type="checkbox" checked={pendingIds.length > 0 && sel.length === pendingIds.length} onChange={(e) => setSel(e.target.checked ? pendingIds : [])} /></th>
              <th className="p-3">Date</th><th className="p-3">Employee</th><th className="p-3">Vendor</th>
              <th className="p-3 text-right">Amount</th><th className="p-3">Ranch</th><th className="p-3">Job Cat</th>
              <th className="p-3">Expense</th><th className="p-3">Reimb?</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-stone-500 font-bold uppercase">No expenses this week</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-stone-200" data-testid={`expense-row-${r.id}`}>
                <td className="p-3">{r.status === "pending" && <input data-testid={`exp-select-${r.id}`} type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} />}</td>
                <td className="p-3 font-mono text-xs">{r.receipt_date}{r.week_start && r.week_start < week && <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold uppercase bg-orange-100 text-orange-700" data-testid={`prior-week-${r.id}`}>Prior Wk</span>}</td>
                <td className="p-3 font-bold">{r.employee_name}</td>
                <td className="p-3">{r.vendor || "—"}</td>
                <td className="p-3 text-right font-mono font-bold">{money(r.amount)}</td>
                <td className="p-3 text-xs">{r.ranch_name || "—"}</td>
                <td className="p-3 text-xs">{r.job_category}</td>
                <td className="p-3 text-xs">{r.expense_category}</td>
                <td className="p-3 text-xs font-bold">{r.needs_reimbursement ? "Yes" : "No"}</td>
                <td className="p-3"><span className={`px-2 py-1 text-[10px] font-bold uppercase ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span></td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`exp-view-${r.id}`} onClick={() => setViewing(r)} title="View Receipt" className="h-8 w-8 flex items-center justify-center border border-stone-300"><Eye size={14} /></button>
                    {r.status !== "approved" && <button data-testid={`exp-approve-${r.id}`} onClick={() => act(r.id, "approve")} title="Approve" className="h-8 w-8 flex items-center justify-center border border-emerald-300 text-emerald-700"><Check size={14} /></button>}
                    {r.status !== "approved" && <button data-testid={`exp-reject-${r.id}`} onClick={() => act(r.id, "reject", true)} title="Reject" className="h-8 w-8 flex items-center justify-center border border-red-300 text-red-700"><X size={14} /></button>}
                    {r.status !== "approved" && <button data-testid={`exp-return-${r.id}`} onClick={() => act(r.id, "return", true)} title="Return to Employee" className="h-8 w-8 flex items-center justify-center border border-stone-300"><CornerUpLeft size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && <ReceiptModal expense={viewing} onClose={() => setViewing(null)} onReimbursement={setReimb} canManage />}
    </div>
  );
}
